import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { useHelp } from "@/components/help/HelpProvider";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const ACCOUNT_META = {
  partner: { portal_label: "Partner Portal", referrals: "Referrals" },
  vendor: { portal_label: "Vendor Portal", referrals: "Quotes & Jobs" },
  facility: { portal_label: "Facility Portal", referrals: "Referrals" },
  professional: { portal_label: "Professional Portal", referrals: "Reviews" },
};

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...p} />;
};

export default function ExternalLayout() {
  const { user, logout } = useAuth();
  const { openHelp } = useHelp();
  const at = user?.account_type || "partner";
  const meta = ACCOUNT_META[at] || ACCOUNT_META.partner;
  const nav = [
    { to: "/external", label: "Dashboard", icon: "LayoutDashboard", end: true },
    { to: "/external/referrals", label: meta.referrals, icon: "Handshake" },
    { to: "/external/documents", label: "Documents", icon: "FileText" },
    ...(at === "professional" ? [{ to: "/external/reviews-signings", label: "My Reviews & Signings", icon: "Scale" }] : []),
    { to: "/external/meetings", label: "Meetings", icon: "Video" },
    { to: "/external/messages", label: "Messages", icon: "MessagesSquare" },
    { to: "/external/notifications", label: "Notifications", icon: "Bell" },
    { to: "/external/profile", label: "Profile", icon: "Building2" },
  ];
  const initials = (user?.organization || user?.full_name || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F7FAFC" }} data-testid="external-layout">
      <header className="sticky top-0 z-30 border-b backdrop-blur" style={{ backgroundColor: "rgba(253,253,254,0.95)", borderColor: "var(--border)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Logo size={36} withText />
          <span className="hidden sm:inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ml-1"
            style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }} data-testid="external-portal-label">{meta.portal_label}</span>
          <div className="ml-auto flex items-center gap-1">
            <button data-testid="external-help-button" onClick={() => openHelp()} title="Help"
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]">
              <Icons.LifeBuoy size={18} />
            </button>
            <AccessibilityToolbar />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[rgba(28,67,88,0.06)] transition-colors" data-testid="external-user-menu">
                  <Avatar className="h-8 w-8"><AvatarFallback style={{ backgroundColor: "var(--mmm-navy)", color: "white", fontSize: 12 }}>{initials}</AvatarFallback></Avatar>
                  <span className="hidden sm:block text-sm font-medium text-[#252221] max-w-[160px] truncate">{user?.organization || user?.full_name}</span>
                  <Icons.ChevronDown size={15} className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-[#9C1E22]" data-testid="external-logout-button">
                  <Icons.LogOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 overflow-x-auto mmm-scroll">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                data-testid={`external-nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive ? "border-[#9C1E22] text-[#9C1E22]" : "border-transparent text-muted-foreground hover:text-[#1C4358]"
                  }`
                }>
                <Icon name={item.icon} size={16} /> {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 mmm-fade-up">
        <Outlet />
      </main>
    </div>
  );
}
